/**
 * Dropping a contour file onto the map.
 *
 * This is the whole of what used to be an import button in the toolbar. A permanent glyph
 * over the imagery whose only job is to open the system file picker is a slot spent on a
 * gesture the browser already provides — and the drop tells you where the contour is going
 * while you are still holding the file, which the picker cannot.
 *
 * NOTHING ABOUT THE PARSING CHANGED. `parseContourFile` and its dictionary (lib/geo.ts) and
 * the server's own import endpoint are untouched; this module is the entrance and the
 * sentence that comes back, nothing else.
 *
 * `dragover` must be cancelled or the browser navigates to the file and the app is gone —
 * that, not the styling, is why the listener exists at all.
 */

import { parseContourFile, type ImportedFeature } from "../lib/geo";
import { T } from "../strings";

/** What was created and what was refused, in the words the toolbar prints. */
export interface DropOutcome {
  note: string | null;
  error: string | null;
}

/** Only a file drag: dragging a selection of text across the map must change nothing. */
export const isFileDrag = (transfer: DataTransfer | null): boolean =>
  Array.from(transfer?.types ?? []).includes("Files");

export const attachFileDrop = (
  element: HTMLElement,
  onFile: (file: File) => void,
): (() => void) => {
  const onDragOver = (event: DragEvent): void => {
    if (!isFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";
    element.classList.add("k-map--dropping");
  };
  const onDragLeave = (event: DragEvent): void => {
    // `relatedTarget` inside the map means the pointer crossed onto a child (a tile, a
    // contour), not out of the map: without this the outline flickers on every tile edge.
    if (element.contains(event.relatedTarget as Node | null)) return;
    element.classList.remove("k-map--dropping");
  };
  const onDrop = (event: DragEvent): void => {
    if (!isFileDrag(event.dataTransfer)) return;
    event.preventDefault();
    element.classList.remove("k-map--dropping");
    const file = event.dataTransfer?.files?.[0];
    if (file) onFile(file);
  };

  element.addEventListener("dragover", onDragOver);
  element.addEventListener("dragleave", onDragLeave);
  element.addEventListener("drop", onDrop);
  return () => {
    element.removeEventListener("dragover", onDragOver);
    element.removeEventListener("dragleave", onDragLeave);
    element.removeEventListener("drop", onDrop);
    element.classList.remove("k-map--dropping");
  };
};

/**
 * Parse a dropped file and create what it holds.
 *
 * A file with no polygons in it is an ERROR and not an empty success: dropping a photograph
 * on the map has to say something, and «создано полей: 0» is not that something.
 */
export const importDroppedFile = async (
  file: File,
  importFeatures: (features: ImportedFeature[]) => Promise<{ created: number; failed: string[] }>,
): Promise<DropOutcome> => {
  const parsed = parseContourFile(file.name, await file.text());
  if (parsed.features.length === 0) {
    return {
      note: null,
      error:
        // «без имени», not "?": a bare question mark on screen reads as a bug (drop-noname).
        parsed.skipped
          .map((item) => `${item.name ?? T.import.unnamed}: ${item.reason}`)
          .join("; ") || T.import.nothingToImport,
    };
  }
  const { created, failed } = await importFeatures(parsed.features);
  const notes = [T.import.created(created)];
  if (parsed.skipped.length > 0) notes.push(T.import.skipped(parsed.skipped.length));
  return { note: notes.join(", "), error: failed.length > 0 ? failed.join("; ") : null };
};
